import type * as client from "../../../client";
import type { CellMarker } from "./types";

// Cell values for Sindri's replacement cost. Sindri's display rules
// (docs/displaying_luminor_rc.md in the sindri repo) are a contract: a row
// withheld pending review carries a luminor_rc that must never be shown as a
// valuation. The confidence wording below is that page's; the reason labels
// shorten its reason table.

type SindriRow = client.SindriRow | null | undefined;
type DisplayStatus = NonNullable<client.SindriRow["display_status"]>;
type DisplayConfidence = NonNullable<client.SindriRow["display_confidence"]>;

export const RC_PENDING_REVIEW = "Pending review";
export const RC_NOT_SCORED = "Not scored";

const STATUS_LABELS: Record<DisplayStatus, string> = {
  show: "Show",
  show_with_caution: "Caution",
  withhold_pending_review: RC_PENDING_REVIEW,
};

const STATUS_COLORS: Record<DisplayStatus | "not_scored", string> = {
  show: "#16a34a", // green
  show_with_caution: "#d97706", // amber
  withhold_pending_review: "#64748b", // slate
  not_scored: "#64748b", // slate
};

export const RC_COLOR_LEGEND =
  "Green: show. Amber: caution. Grey: pending review or not scored.";

const CONFIDENCE_MEANINGS: Record<DisplayConfidence, string> = {
  High: "A calibrated peer group with enough accounts behind it, no cap or review reason fired.",
  Standard:
    "Treat as an estimate to be checked against the submission, not a figure to rely on.",
};

const REASON_LABELS: Record<string, string> = {
  // Withhold
  no_signal: "no estimate",
  psf_below_absolute_band: "$/sqft below any plausible building cost",
  psf_above_absolute_band: "$/sqft above any plausible building cost",
  engine_far_above_external_band: "far above the benchmark cost range",
  construction_code_restricted: "construction implausible for the occupancy",
  engine_over_broker_high: "over 5x the stated building value",
  engine_over_broker_low: "under 0.2x the stated building value",
  land_or_vacant_indicator: "SOV describes land, yet a building was priced",
  land_text_imputed_size: "land with a size the broker did not state",
  imputed_sqft_contradicted: "imputed sqft contradicted by the stated value",
  shared_footprint_sqft: "footprint shared with other locations",
  // Caution
  construction_rare_for_occupancy: "construction rare for the occupancy",
  stated_value_far_below_engine: "stated value far below the estimate",
  stated_below_es_low: "stated value below the expected range",
  no_building_value: "no building value on the SOV (reference only)",
  contents_or_leased_indicator: "contents-only or leased (reference only)",
  low_confidence: "lower confidence",
  size_conflict: "broker sqft conflicts with footprint or assessor",
  tiv_suspected: "building value may be the total insured value",
  pattern_2_basis: "stated values on a different basis",
  thin_cell: "small peer group",
  fallback_cell_path: "no calibrated peer group",
  agricultural_structure: "farm building, thin cost evidence",
};

const PLAUSIBILITY_PREFIX = "plausibility_";

/** A reason code as a short phrase. Sindri adds codes without a contract bump,
 * so an unknown code is shown as given. */
export const reasonLabel = (code: string): string => {
  const label = REASON_LABELS[code];
  if (label) return label;
  if (code.startsWith(PLAUSIBILITY_PREFIX)) {
    const flag = code.slice(PLAUSIBILITY_PREFIX.length).replaceAll("_", " ");
    return `plausibility check: ${flag}`;
  }
  return code;
};

const formatUsd = (amount: number): string =>
  amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

/** The estimate in USD, or why there is none. Undefined when Sindri has not
 * scored the report. */
export const replacementCostValue = (
  row: SindriRow
): number | string | undefined => {
  if (!row) return undefined;
  if (row.error) return RC_NOT_SCORED;
  if (row.display_status === "withhold_pending_review")
    return RC_PENDING_REVIEW;
  if (row.luminor_rc == null)
    return `No value (${row.failure_reason ?? "unknown"})`;
  return row.luminor_rc;
};

/** The status as the value's marker: its color and the dot's word for it. */
export const replacementCostMarker = (
  row: SindriRow
): CellMarker | undefined => {
  if (!row) return undefined;
  if (row.error)
    return { color: STATUS_COLORS.not_scored, label: RC_NOT_SCORED };
  if (!row.display_status) return undefined;
  return {
    color: STATUS_COLORS[row.display_status],
    label: STATUS_LABELS[row.display_status],
  };
};

/** Everything behind the value, reason first: the status in the dot's words
 * with its reasons, how the value was built (or why there is none), then the
 * confidence. A withheld value is opened for a reviewer, labelled as not a
 * valuation. */
export const replacementCostTooltip = (row: SindriRow): string | undefined => {
  if (!row) return undefined;
  if (row.error)
    return `${RC_NOT_SCORED}: Sindri could not score this location (${row.error.reason}).`;
  const headline: string[] = [];
  if (row.display_status) {
    const reasons = (row.display_reasons ?? []).map(reasonLabel);
    const label = STATUS_LABELS[row.display_status];
    headline.push(
      reasons.length > 0 ? `${label}: ${reasons.join("; ")}` : label
    );
  }
  if (
    row.display_status === "withhold_pending_review" &&
    row.luminor_rc != null
  ) {
    headline.push(
      `Withheld value ${formatUsd(row.luminor_rc)}: not a valuation until reviewed.`
    );
  }
  const confidence = row.display_confidence
    ? `Confidence: ${row.display_confidence}. ${CONFIDENCE_MEANINGS[row.display_confidence]}`
    : "";
  const sections = [
    headline.join("\n"),
    row.explanation_text ?? "",
    confidence,
  ].filter((section) => section !== "");
  return sections.length > 0 ? sections.join("\n\n") : undefined;
};

// ITV percentages worth a second look: below, likely under-insured; above, a
// stated value well over the estimate.
const ITV_UNDER_INSURED_BELOW = 80;
const ITV_CHECK_ABOVE = 150;
const ITV_COLORS = {
  underInsured: "#dc2626", // red
  check: "#d97706", // amber
};

// Sindri leaves the ratio null whenever an ITV must not be shown.
const itvPercent = (row: SindriRow): number | undefined => {
  const ratio = row?.explanation?.itv_ratio;
  return ratio == null ? undefined : Math.round(ratio * 100);
};

/** Stated building value over the Luminor RC, as a percentage. */
export const replacementCostItvPercent = (
  row: SindriRow
): string | undefined => {
  const percent = itvPercent(row);
  return percent == null ? undefined : `${percent}%`;
};

export const replacementCostItvMarker = (
  row: SindriRow
): CellMarker | undefined => {
  const percent = itvPercent(row);
  if (percent == null) return undefined;
  if (percent < ITV_UNDER_INSURED_BELOW)
    return { color: ITV_COLORS.underInsured, label: "Likely under-insured" };
  if (percent > ITV_CHECK_ABOVE)
    return { color: ITV_COLORS.check, label: "Worth a check" };
  return undefined;
};

export const replacementCostItvTooltip = (
  row: SindriRow
): string | undefined => {
  const percent = itvPercent(row);
  if (percent == null) return undefined;
  if (percent < ITV_UNDER_INSURED_BELOW)
    return `Below ${ITV_UNDER_INSURED_BELOW}%: likely under-insured.`;
  if (percent > ITV_CHECK_ABOVE)
    return `Above ${ITV_CHECK_ABOVE}%: the stated value is well above the estimate. Worth a check.`;
  return undefined;
};

export const RC_ITV_LEGEND = `Red: below ${ITV_UNDER_INSURED_BELOW}%, likely under-insured. Amber: above ${ITV_CHECK_ABOVE}%, worth a check.`;

// ITV bands for the pivot, on the same thresholds as the markers. The split at
// 100% separates under from over within the unmarked range.
export const RC_ITV_UNDER_INSURED = `Below ${ITV_UNDER_INSURED_BELOW}%`;
export const RC_ITV_CHECK = `Above ${ITV_CHECK_ABOVE}%`;
export const RC_NO_ITV = "No ITV";
export const RC_ITV_BANDS: readonly string[] = [
  RC_ITV_UNDER_INSURED,
  `${ITV_UNDER_INSURED_BELOW}% - 100%`,
  `100% - ${ITV_CHECK_ABOVE}%`,
  RC_ITV_CHECK,
  RC_NO_ITV,
];

/** The ITV band. A scored row Sindri gives no ratio for (withheld, no building
 * value, contents or leased) is ``RC_NO_ITV``; undefined before Sindri has
 * scored the report. */
export const replacementCostItvBand = (row: SindriRow): string | undefined => {
  if (!row) return undefined;
  const percent = itvPercent(row);
  if (percent == null) return RC_NO_ITV;
  if (percent < ITV_UNDER_INSURED_BELOW) return RC_ITV_UNDER_INSURED;
  if (percent < 100) return `${ITV_UNDER_INSURED_BELOW}% - 100%`;
  if (percent <= ITV_CHECK_ABOVE) return `100% - ${ITV_CHECK_ABOVE}%`;
  return RC_ITV_CHECK;
};
